import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ChatMessage } from '../ask.js';

/**
 * Questions about the application (prd-deltas §24). Answers name parts as [[id]] and
 * cite code as `path:line`; both are links here, to the part on the canvas and to the
 * line in the code. Nothing asked here changes the project.
 */

export type ChatState =
  | { status: 'loading' }
  | { status: 'ready'; chatId: string | null; messages: ChatMessage[]; asking?: string; error?: string }
  | { status: 'error'; message: string };

export function Chat({
  state,
  onAsk,
  onNew,
  isPart,
  isFile,
  onOpenPart,
  onOpenFile,
}: {
  state: ChatState | undefined;
  onAsk: (question: string) => void;
  onNew: () => void;
  isPart: (id: string) => boolean;
  isFile: (path: string) => boolean;
  onOpenPart: (id: string) => void;
  onOpenFile: (path: string, line?: number) => void;
}) {
  const [draft, setDraft] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const ready = state?.status === 'ready' ? state : undefined;
  const asking = ready?.asking;

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [ready?.messages.length, asking]);

  const send = () => {
    const question = draft.trim();
    if (!question || asking) return;
    onAsk(question);
    setDraft('');
  };

  const links = { isPart, isFile, onOpenPart, onOpenFile };

  return (
    <div className="page chat">
      <div className="chat-log" aria-live="polite">
        {!state || state.status === 'loading' ? <p className="muted">Loading the conversation…</p> : null}
        {state?.status === 'error' ? <p className="chat-error">{state.message}</p> : null}
        {ready && ready.messages.length === 0 && !asking ? (
          <div className="chat-empty">
            <p className="chat-empty-lead">Ask anything about this application.</p>
            <p className="muted">
              How a request travels, where something is stored, what a part is for. Answers name the
              parts and cite the code; nothing here changes the project.
            </p>
          </div>
        ) : null}
        {ready?.messages.map((message, i) => (
          <Message key={i} message={message} links={links} />
        ))}
        {asking ? (
          <>
            <Message message={{ role: 'user', content: asking }} links={links} />
            <p className="chat-thinking">Reading…</p>
          </>
        ) : null}
        {ready?.error ? <p className="chat-error">{ready.error}</p> : null}
        <div ref={end} />
      </div>

      <form
        className="chat-ask"
        onSubmit={(event) => {
          event.preventDefault();
          send();
        }}
      >
        <textarea
          value={draft}
          rows={2}
          placeholder="Ask about this application"
          aria-label="Question"
          disabled={!ready}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // Enter asks; Shift+Enter is a new line, as in every chat.
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              send();
            }
          }}
        />
        <div className="chat-ask-actions">
          <button type="button" className="text-button" onClick={onNew} disabled={!ready || !!asking || ready.messages.length === 0}>
            New conversation
          </button>
          <button type="submit" className="chat-send" disabled={!ready || !!asking || draft.trim() === ''}>
            Ask
          </button>
        </div>
      </form>
    </div>
  );
}

type Links = {
  isPart: (id: string) => boolean;
  isFile: (path: string) => boolean;
  onOpenPart: (id: string) => void;
  onOpenFile: (path: string, line?: number) => void;
};

function Message({ message, links }: { message: ChatMessage; links: Links }) {
  if (message.role === 'user') {
    return <div className="chat-question">{message.content}</div>;
  }
  return (
    <div className="chat-answer">
      {blocks(message.content).map((block, i) =>
        block.kind === 'list' ? (
          <ul key={i}>
            {block.items.map((item, j) => (
              <li key={j}>{inline(item, links)}</li>
            ))}
          </ul>
        ) : (
          <p key={i}>{inline(block.text, links)}</p>
        ),
      )}
      {message.reads && message.reads.length > 0 ? (
        <p className="chat-reads">
          Read {message.reads.map((path, i) => (
            <span key={path}>
              {i > 0 ? ', ' : ''}
              <button type="button" className="chat-file" onClick={() => links.onOpenFile(path)}>{path}</button>
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
}

type Block = { kind: 'para'; text: string } | { kind: 'list'; items: string[] };

/** Paragraphs and bulleted or numbered lists: all an answer is asked to use. */
function blocks(text: string): Block[] {
  const out: Block[] = [];
  for (const chunk of text.split(/\n{2,}/)) {
    const lines = chunk.split('\n').filter((line) => line.trim() !== '');
    if (lines.length === 0) continue;
    const item = /^\s*(?:[-*•]|\d+[.)])\s+/;
    if (lines.every((line) => item.test(line))) {
      out.push({ kind: 'list', items: lines.map((line) => line.replace(item, '')) });
    } else {
      out.push({ kind: 'para', text: lines.join(' ') });
    }
  }
  return out;
}

/** [[part]] links to the part; `path:line` links to the code; **bold** and `code` read as such. */
function inline(text: string, links: Links): ReactNode[] {
  return text.split(/(\[\[[^\]\n]+\]\]|`[^`\n]+`|\*\*[^*\n]+\*\*)/).map((piece, i) => {
    const part = /^\[\[([^\]]+)\]\]$/.exec(piece);
    if (part) {
      const id = part[1]!.trim();
      return links.isPart(id) ? (
        <button key={i} type="button" className="chat-part" onClick={() => links.onOpenPart(id)}>{id}</button>
      ) : (
        <span key={i} className="chat-part is-unknown">{id}</span>
      );
    }
    const code = /^`([^`]+)`$/.exec(piece);
    if (code) {
      const cited = /^([\w./@+-]+?)(?::(\d+)(?:-\d+)?)?$/.exec(code[1]!);
      if (cited && links.isFile(cited[1]!)) {
        const line = cited[2] ? Number(cited[2]) : undefined;
        return (
          <button key={i} type="button" className="chat-file" onClick={() => links.onOpenFile(cited[1]!, line)}>
            {code[1]}
          </button>
        );
      }
      return <code key={i}>{code[1]}</code>;
    }
    const bold = /^\*\*([^*]+)\*\*$/.exec(piece);
    if (bold) return <strong key={i}>{bold[1]}</strong>;
    return piece;
  });
}
